/**
 * TruePrice Canvas Navigation — CanvasLayout
 * 
 * Single source of truth for page state.
 * - Fixed teal header with fading title
 * - Settings button stays in place
 * - Content swipes beneath header
 * 
 * NOTE: New button navigation is now handled internally by CanvasBottomNav
 * via NewActionMenu (no longer needs onNavigateToCalculator prop)
 */

import React, { useCallback, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwipeablePageContainer, type CanvasPage } from './SwipeablePageContainer';
import { CanvasBottomNav } from './CanvasBottomNav';
import { OfflineBanner } from '@/components/OfflineBanner';
import { SettingsPanel } from '@/components/SettingsPanel';
import { useDashboardMode } from '@/hooks/useDashboardMode';

interface CanvasLayoutProps {
  initialPage?: number;
  onPageChange?: (pageId: string, index: number) => void;
  dashboardContent: React.ReactNode;
  portfolioContent?: React.ReactNode;
  insightsContent?: React.ReactNode;
  className?: string;
  dashboardBadge?: number;
  portfolioBadge?: number;
}

const PAGE_TITLES = ['Dashboard', 'Portfolio', 'Insights'];

export function CanvasLayout({
  initialPage = 0,
  onPageChange,
  dashboardContent,
  portfolioContent,
  insightsContent,
  className = '',
  dashboardBadge = 0,
  portfolioBadge = 0,
}: CanvasLayoutProps) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { mode: dashboardMode, setMode: setDashboardMode } = useDashboardMode();

  // Listen for open-settings events
  useEffect(() => {
    const handler = () => setSettingsOpen(true);
    window.addEventListener('open-settings', handler);
    return () => window.removeEventListener('open-settings', handler);
  }, []);

  // Sync with external initialPage changes
  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  const pages: CanvasPage[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      component: dashboardContent,
    },
    {
      id: 'portfolio',
      label: 'Portfolio',
      component: portfolioContent || dashboardContent,
    },
    {
      id: 'insights',
      label: 'Insights',
      component: insightsContent || dashboardContent,
    },
  ];

  // Handle page change from swipe (no fade)
  const handlePageChange = useCallback((index: number) => {
    setCurrentPage(index);
    onPageChange?.(pages[index].id, index);
  }, [pages, onPageChange]);

  // Handle page change from nav tab tap (same slide as swipe — no fade)
  const handleNavSelect = useCallback((index: number) => {
    if (index >= pages.length) return;
    if (index === currentPage) return;
    setCurrentPage(index);
    onPageChange?.(pages[index].id, index);
  }, [currentPage, pages, onPageChange]);

  return (
    <div className={`relative h-full bg-cream-100 overflow-x-hidden ${className}`}>
      {/* Offline Banner (global) */}
      <OfflineBanner />

      {/* Fixed teal header */}
      <div 
        className="fixed top-0 left-0 right-0 z-20 bg-teal-500"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="h-14 max-w-lg mx-auto px-4 flex items-center justify-between">
          {/* Left spacer */}
          <div className="w-10" />
          
          {/* Animated title */}
          <div className="relative h-6 flex-1 flex items-center justify-center">
            <AnimatePresence mode="popLayout">
              <motion.h1
                key={currentPage}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
                className="text-lg font-semibold text-white absolute"
              >
                {PAGE_TITLES[currentPage]}
              </motion.h1>
            </AnimatePresence>
          </div>
          
          {/* Settings button */}
          <button
            onClick={() => setSettingsOpen(true)}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95 hover:bg-white/10 active:bg-white/20"
            aria-label="Settings"
          >
            <svg 
              className="w-5 h-5 text-white" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Swipeable pages - with top padding for fixed header */}
      <div
        className="h-full overflow-x-hidden"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 56px)' }}
      >
        <SwipeablePageContainer
          pages={pages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          className="h-full"
        />
      </div>
      
      {/* Bottom nav - handles New button internally via NewActionMenu */}
      <CanvasBottomNav
        currentPage={currentPage}
        onPageSelect={handleNavSelect}
        dashboardBadge={dashboardBadge}
        portfolioBadge={portfolioBadge}
      />

      {/* Settings slide-over */}
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} dashboardMode={dashboardMode} onDashboardModeChange={setDashboardMode} />
    </div>
  );
}

export default CanvasLayout;
